/**
 * Styles for legacy remotes that render inside a shadow root.
 *
 * Remotes built with @shipbob/ui-app's `mfeMountWrapperPlugin` (UserManagementUI,
 * ReportingUI, InventoryUI, ...) attach a shadow root to the element the host
 * hands `mount()` and adopt their own stylesheets into it. PrimeVue's styled
 * mode (`@primeuix/styled`, shared) injects every component/theme sheet into
 * `document.head` as `<style data-primevue-style-id>`, which a shadow root
 * cannot see. Each registered shadow root therefore gets a clone of every such
 * sheet: the ones already present at registration and the ones injected later.
 * The design system's own theme tags (plain ids) are cloned the same way. This
 * is MerchantApp's `PrimeVueStyleInjection`, same exclusions.
 *
 * Only PRODUCTION builds of a remote are supported under this host. The
 * wrapper's dev branch (`vp dev`) moves every unmarked `<style>` in
 * `document.head` into the remote's shadow root on the assumption that the page
 * is the remote's own harness, which strips the host and every other remote of
 * their Vite dev sheets. The host does not work around that: test a shadow-DOM
 * remote hosted from its production build (`vp build` + `vp preview`, see the
 * README). Likewise, `@import`s inside sheets a remote adopts (icon fonts) are
 * not hoisted into the document; MerchantApp never did either, the design
 * system's own document-level sheet carries the fonts.
 */

const PRIMEVUE_STYLE_ATTR = "data-primevue-style-id";
/** Design-system theme tags cloned by id; they carry no PrimeVue attribute. */
const DESIGN_SYSTEM_STYLE_IDS = ["shipbob-root-theme", "shipbob-legacy-theme"];
/** Same exclusions as MerchantApp: theme variables and the tooltip sheet are not cloned. */
const IGNORED_STYLE_IDS = ["variables", "tooltip"];
/** The attribute @shipbob/ui-app's mount wrapper uses to skip a style node it did not adopt. */
const WRAPPER_ADOPTED_ATTR = "data-mfe-adopted";

const shadowRoots = new Set<ShadowRoot>();
let headObserver: MutationObserver | null = null;

function isStyleNode(node: Node): node is HTMLStyleElement | HTMLLinkElement {
  return node instanceof HTMLStyleElement || node instanceof HTMLLinkElement;
}

function primeVueStyleId(node: Element): string | null {
  return node.getAttribute(PRIMEVUE_STYLE_ATTR);
}

function cloneInto(node: HTMLStyleElement | HTMLLinkElement, root: ShadowRoot) {
  const styleId = primeVueStyleId(node);
  if (!styleId) return;
  if (IGNORED_STYLE_IDS.some((ignored) => styleId.includes(ignored))) return;
  if (root.querySelector(`[${PRIMEVUE_STYLE_ATTR}="${styleId}"]`)) return;
  const clone = node.cloneNode(true) as Element;
  clone.setAttribute(WRAPPER_ADOPTED_ATTR, "");
  root.appendChild(clone);
}

function cloneDesignSystemStylesInto(root: ShadowRoot) {
  for (const id of DESIGN_SYSTEM_STYLE_IDS) {
    const style = document.getElementById(id);
    if (style && !root.querySelector(`#${id}`)) root.appendChild(style.cloneNode(true));
  }
}

function onHeadMutations(mutations: MutationRecord[]) {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!isStyleNode(node) || !primeVueStyleId(node)) continue;
      for (const root of shadowRoots) cloneInto(node, root);
    }
  }
}

/**
 * Starts watching `document.head` for PrimeVue sheets injected after a shadow
 * root registered. Idempotent; `main.ts` calls it at bootstrap and
 * `LegacyRemoteMount` before every mount, so a frame that renders a legacy
 * remote outside this host's bootstrap (a routes-mode remote's harness using
 * the exposed PageFrame) is covered too.
 */
export function installLegacyShadowStyles() {
  if (headObserver) return;
  headObserver = new MutationObserver(onHeadMutations);
  headObserver.observe(document.head, { childList: true });
}

/** Test-only. */
export function uninstallLegacyShadowStyles() {
  headObserver?.disconnect();
  headObserver = null;
  shadowRoots.clear();
}

/** Clone the PrimeVue and design-system sheets into a remote's shadow root and keep it current. */
export function registerShadowRoot(root: ShadowRoot) {
  if (shadowRoots.has(root)) return;
  shadowRoots.add(root);
  for (const node of document.head.querySelectorAll(`[${PRIMEVUE_STYLE_ATTR}]`)) {
    if (isStyleNode(node)) cloneInto(node, root);
  }
  cloneDesignSystemStylesInto(root);
}

export function unregisterShadowRoot(root: ShadowRoot) {
  shadowRoots.delete(root);
}
